import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../../u2000Core/axios/U2000UseAxiosAuthenticated';
import U2000BasicTable, { type U2000SearchPayload, type U2000SearchResult } from '../../../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000IdColumnDef, type U2000TableColumn } from '../../../u2000Core/components/tables/basic/U2000BasicTableList';
import React, { useEffect } from 'react';
import { useToast } from '../../../u2000Core/context/toast/U2000ToastContext';
import { U2000HandleApiError } from '../../../u2000Core/axios/U2000HandleApiError';
import { useU2000SearchHandlers } from '../../../u2000Core/hooks/handler/U2000UseSearchHandlers';
import { useNavigate } from 'react-router-dom';
import type { TFunction } from 'i18next';
import type { U2000TableHandlers } from '../../../u2000Core/types/U2000TableHandlers';
import { U2000GridStateEnumPaginationDirection } from '../../../u2000Core/reducer/formReducer/U2000GridState';
import { U2000LUStateEnumNextAction } from '../../../u2000Core/reducer/formReducer/U2000LUState';
import { useU2000DeleteHandlers } from '../../../u2000Core/hooks/handler/U2000UseDeleteHandlers';
import { U2000GridStateDeleteAction, type U2000GridStateNextPageAfterDeleteReturn } from '../../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { useU3302Context } from '../../U3302Context';
import { doDeleteCategory, doSearchCategories, luHandleAppiError, type U3302CategorySearchRow } from '../../U3302Service';
import { NS } from '../../U3302';
import U3302CategoryDeleteDlg from '../common/U3302CategoryDeleteDlg';
import { U3302CategoryListToolBar } from './U3302CategoryListToolBar';
import { useU2000LogicalUnitContext } from '../../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';

function parseBooleanGridValue(value: unknown): boolean | null {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  return null;
}

function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: 'code',
      label: t('lblCode', { ns: NS }),
      widthClass: 'w-[160px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: true,
    },
    {
      name: 'label',
      label: t('lblLabel', { ns: NS }),
      widthClass: 'w-[220px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'effectiveColorCode',
      label: t('lblEffectiveColor', { ns: NS }),
      widthClass: 'w-[120px]',
      type: 'string',
      align: 'center',
      isCriteriaField: false,
      inGridField: true,
      isDefaultColumn: true,
      sortable: false,
      isDefaultSortedColumn: false,
      display: (_column, value) => {
        const color = String(value ?? '');
        return (
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded-full border border-gray-300" style={{ backgroundColor: color }} />
            <span className="text-xs text-gray-600">{color}</span>
          </span>
        );
      },
    },
    {
      name: 'origin',
      label: t('lblOrigin', { ns: NS }),
      widthClass: 'w-[120px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      includeConditions: ['equals', 'notequals'],
      display: (_column, value) => t(`valOrigin${String(value ?? '')}`, { ns: NS }),
    },
    {
      name: 'dateMode',
      label: t('lblDateMode', { ns: NS }),
      widthClass: 'w-[140px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      includeConditions: ['equals', 'notequals'],
      display: (_column, value) => t(`valDateMode${String(value ?? 'SINGLE')}`, { ns: NS }),
    },
    {
      name: 'active',
      label: t('lblActive', { ns: NS }),
      widthClass: 'w-[100px]',
      type: 'boolean',
      align: 'center',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      includeConditions: ['equals', 'notequals'],
      display: (_column, value) => {
        const b = parseBooleanGridValue(value);
        if (b === true) return t('lblActiveYes', { ns: NS });
        if (b === false) return t('lblActiveNo', { ns: NS });
        return null;
      },
    },
  ];
}

const U3302CategoryList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3302Context();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  useEffect(() => {
    const cols = getColumnsDef(t);
    searchStateHandlers.setColumns(cols);
    gridStateHandlers.setColumns(cols);
    setLogicalUnitBreadcrumb([{ label: t('title', { ns: NS }) }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const handleSearch = useU2000SearchHandlers<U3302CategorySearchRow>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => {
      return await doSearchCategories(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3302CategorySearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as never, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => (gridStateHandlers ? gridStateHandlers.getSelectedRows() : []),
    doDeleteFn: async (_id, row) => {
      if (row.origin === 'SYSTEM') {
        throw new Error('SYSTEM_DELETE');
      }
      return await doDeleteCategory(u2000AxiosAuthenticated, { id: row.id, version: row.version });
    },
    onSuccess: (deletedRows) => {
      const page = gridStateHandlers.getCurrentPage();
      let newData = gridStateHandlers.getData();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t('success0003', { key: row.code ?? row.id, ns: NS }), t('successTitle', { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      const action: U2000GridStateNextPageAfterDeleteReturn = gridStateHandlers.getNextPageAfterDelete(page, totalRows);
      if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.NEXTPAGE,
        );
      }
    },
    onError: (errors) => {
      errors.forEach(({ error }) => {
        if (error instanceof Error && error.message === 'SYSTEM_DELETE') {
          toast.error(t('errSystemCategoryDelete', { ns: NS }), t('errorTitle', { ns: NS }));
          return;
        }
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  const listHandlerActions: U2000TableHandlers<U3302CategorySearchRow> = {
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onRowClick: (row: U3302CategorySearchRow) => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      luStateHandlers.setActionOriginFrom('');
      const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === row.id);
      gridStateHandlers.setCurrentIdx(currentIdx);
      navigate(`/U3302/${row.id}`);
    },
    onAdd: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
      luStateHandlers.setActionOriginFrom('/U3302');
      navigate('/U3302/new');
    },
    onModify: () => {
      const row = gridStateHandlers.getSelectedRows()[0];
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
      luStateHandlers.setActionOriginFrom('/U3302');
      navigate(`/U3302/${row?.id}`);
    },
    onDelete: () => {
      handleDelete.handleDelete();
    },
  };

  if (searchStateHandlers.getColumns().length === 0) {
    return null;
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 p-3">
      <U3302CategoryDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000BasicTable
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        actionsButtons={<U3302CategoryListToolBar gridStateHandlers={gridStateHandlers} handlerActions={listHandlerActions} />}
        handlerActions={listHandlerActions}
        label={t('lblCategoryList', {
          count: gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
        allowRowSelection={true}
        hasExportRole={false}
      />
    </div>
  );
};

export default U3302CategoryList;
